import type { InputHTMLAttributes, Ref } from 'react';
import { cx } from '../cx';
import { Icon } from '../Icon/Icon';

/**
 * A SEARCH FIELD, AND THE KIT DRAWS IT THREE WAYS (wave 4 round 1, C2-18; owner on the
 * three fields side by side: "ob das akkurat ist, dass die Dinge dunkel sind, auch dass die
 * Searches hier dunkel sind statt wieder Liquid fuer die anderen Searches oder hellgrau. Es
 * ist von Seite zu Seite: unterschiedliche Sichtweisen auf aehnliche Bausteine").
 *
 * The three are not a preference, they are what the kits ship, and each one belongs to a
 * PLACE rather than to a screen:
 *
 * - `toolbar`: `Titlebars and Toolbars/{Light,Dark}/XL/Search/Active/Placeholder`, a
 *   159x36 glass capsule with the same fill, blur and rim stack as the toolbar's buttons,
 *   the magnifier at x 10 and the placeholder at x 32 in SF Pro Medium 13.
 * - `sidebar`: `Sidebar/{Light,Dark}/Search/Placeholder` in the iOS kit - the ONLY sidebar
 *   search master either kit ships (searched `search` across all 35 macOS pages, whose hits
 *   are `Search Fields`, `Titlebars and Toolbars` and the Clear Search helper). A flat 44px
 *   capsule in `#EDEDED` / `#121212`, no blur, no rim, magnifier at x 11, value at x 45.
 * - `content`: `Search Fields/{Light,Dark}/5 XL/Empty`, a 36px capsule filled `#FFFFFF` /
 *   `#1E1E1E` with a 1px OUTSIDE hairline at 5 percent / 4 percent.
 *
 * None of the three is the bordered rectangle this product drew in its forms, and the
 * context is DECLARED rather than inferred from where the element happens to be mounted:
 * a class can be overridden and a place can move, and the review has to be able to ask the
 * running product which of the three constructions a field is (`behaviour.spec.ts`, (s)).
 */
export type FieldContext = 'toolbar' | 'sidebar' | 'content';

export interface SearchFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  /** Which of the kit's three shells this field is. */
  readonly context?: FieldContext | undefined;
  /** The accessible name, and the placeholder unless one is given. */
  readonly label: string;
  readonly ref?: Ref<HTMLInputElement> | undefined;
}

export function SearchField({
  context = 'content',
  label,
  placeholder,
  className,
  ref,
  ...rest
}: SearchFieldProps) {
  return (
    <label
      className={cx('field-search', `field-search-${context}`, className)}
      data-field-context={context}
    >
      <Icon name="search" />
      {/* THE FIELD'S OWN FOUR ATTRIBUTES COME LAST, and that is load-bearing rather than
          tidy: a caller can be a floating primitive's trigger, and a trigger merges ITS
          props onto whatever it wraps - `Popover`'s puts `type="button"` on its child,
          which turned this search field into a button in the accessibility tree the first
          time it was used as one. What the field IS cannot be overwritten from outside. */}
      <input
        {...rest}
        ref={ref}
        type="search"
        data-field-context={context}
        aria-label={label}
        placeholder={placeholder ?? label}
      />
    </label>
  );
}
